import { motion } from 'framer-motion';
import { ChefHat, Heart, Leaf, Sparkles } from 'lucide-react';
import { MenuItemCard } from '../components/MenuItemCard';
import { menuCategories } from '../data/menuData';

const SWIGGY_URL = process.env.REACT_APP_SWIGGY_URL;

const popularItems = [
  menuCategories[0].items[2],
  menuCategories[0].items[0],
  menuCategories[2].items[4],
  menuCategories[1].items[0]
];

export default function Home() {
  return (
    <div className="min-h-screen">
      <section
        className="relative min-h-screen flex items-center justify-center bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(253, 251, 247, 0.85), rgba(253, 251, 247, 0.85)), url('https://customer-assets.emergentagent.com/job_chaat-fresh/artifacts/3tog1sfj_Food%20Menu%20Photo%20Collage.png')`
        }}
        data-testid="hero-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-playfair font-bold text-[#8B0000] mb-6 leading-tight">
              Chaat-De-Licious
            </h1>
            <p className="text-xl sm:text-2xl font-caveat text-spice mb-8">
              Handcrafted Gourmet Fun Snacks
            </p>
            <p className="text-base sm:text-lg text-brown-medium max-w-3xl mx-auto mb-6 leading-relaxed">
              At Chaat-De-licious, we bring a premium twist to India's most loved fun food. Our cloud kitchen creates gourmet-style chaat that is healthy, hygienic, and irresistibly delicious using fresh fruits, high-fiber ingredients, and protein-rich elements.
            </p>
            <p className="text-base sm:text-lg text-brown-medium max-w-3xl mx-auto mb-10 leading-relaxed">
              To complement our vibrant dishes, we serve unique summer coolers, crafted with refreshing fruits and natural ingredients—perfectly paired to elevate your chaat experience.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a
                href={SWIGGY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-spice text-white px-8 py-4 rounded-full hover:bg-spice-hover transition-all transform hover:-translate-y-1 hover:shadow-xl font-bold text-lg"
                data-testid="hero-order-now-btn"
              >
                Order Now
              </a>
              <a
                href="/menu"
                className="border-2 border-leaf text-leaf bg-transparent px-8 py-4 rounded-full hover:bg-leaf hover:text-white transition-all font-bold text-lg"
                data-testid="hero-view-menu-btn"
              >
                View Menu
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-16 bg-cream-secondary" data-testid="popular-items-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl sm:text-5xl font-playfair font-bold text-brown-dark mb-4">
              Popular Items
            </h2>
            <p className="text-lg text-brown-medium max-w-2xl mx-auto">
              Our customers' most loved gourmet chaat creations
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {popularItems.map((item, index) => (
              <MenuItemCard key={item.id} item={item} index={index} />
            ))}
          </div>

          <div className="text-center mt-10">
            <a
              href="/menu"
              className="inline-block bg-leaf text-white px-8 py-3 rounded-full hover:bg-leaf-hover transition-all transform hover:-translate-y-1 font-bold"
              data-testid="view-full-menu-btn"
            >
              View Full Menu
            </a>
          </div>
        </div>
      </section>

      <section className="py-16 bg-cream" data-testid="why-choose-us-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl sm:text-5xl font-playfair font-bold text-brown-dark mb-4">
              Why Choose Us
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-cream-secondary rounded-2xl p-8 text-center border-2 border-dashed border-brown-light/30 hover:border-turmeric transition-all"
            >
              <div className="w-16 h-16 bg-turmeric rounded-full flex items-center justify-center mx-auto mb-4">
                <ChefHat size={32} className="text-brown-dark" />
              </div>
              <h3 className="text-xl font-playfair font-bold text-brown-dark mb-3">
                Gourmet Quality
              </h3>
              <p className="text-brown-medium text-sm">
                Premium ingredients crafted with love and expertise
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-cream-secondary rounded-2xl p-8 text-center border-2 border-dashed border-brown-light/30 hover:border-leaf transition-all"
            >
              <div className="w-16 h-16 bg-leaf rounded-full flex items-center justify-center mx-auto mb-4">
                <Leaf size={32} className="text-white" />
              </div>
              <h3 className="text-xl font-playfair font-bold text-brown-dark mb-3">
                Fresh & Healthy
              </h3>
              <p className="text-brown-medium text-sm">
                High-fiber, protein-rich ingredients for guilt-free indulgence
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="bg-cream-secondary rounded-2xl p-8 text-center border-2 border-dashed border-brown-light/30 hover:border-spice transition-all"
            >
              <div className="w-16 h-16 bg-spice rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart size={32} className="text-white" />
              </div>
              <h3 className="text-xl font-playfair font-bold text-brown-dark mb-3">
                Made with Love
              </h3>
              <p className="text-brown-medium text-sm">
                Every dish prepared with passion and care
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="bg-cream-secondary rounded-2xl p-8 text-center border-2 border-dashed border-brown-light/30 hover:border-turmeric transition-all"
            >
              <div className="w-16 h-16 bg-turmeric rounded-full flex items-center justify-center mx-auto mb-4">
                <Sparkles size={32} className="text-brown-dark" />
              </div>
              <h3 className="text-xl font-playfair font-bold text-brown-dark mb-3">
                Hygienic Kitchen
              </h3>
              <p className="text-brown-medium text-sm">
                Cloud kitchen ensuring the highest standards of cleanliness
              </p>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-12 text-center bg-cream-accent/50 rounded-2xl p-8 max-w-4xl mx-auto"
          >
            <p className="text-lg text-brown-dark leading-relaxed">
              At Chaat-De-licious, we believe that indulgence and health can go hand in hand. 
              Our unique summer coolers, crafted with refreshing fruits and natural ingredients, 
              perfectly complement our vibrant chaat dishes.
            </p>
            <p className="text-xl font-playfair font-bold text-spice mt-4">
              Chaat-De-licious — where fun food meets gourmet innovation—fresh, flavourful with Love!
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-16 bg-cream-secondary" data-testid="combo-offers-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl sm:text-5xl font-playfair font-bold text-brown-dark mb-4">
              Combo Offers
            </h2>
            <div className="bg-turmeric/20 border-2 border-dashed border-turmeric rounded-2xl p-12 max-w-2xl mx-auto">
              <p className="text-2xl font-caveat text-brown-dark mb-2">
                Exciting Combos
              </p>
              <p className="text-5xl font-playfair font-bold text-spice">
                Coming Soon!
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}